import React, { useEffect, useState } from 'react';

import { DownloadIcon } from '@vlabs/icons';
import { estimateLivenessDeepfake } from '@vlabs/shared/requests';
import { selectAppService } from '@vlabs/shared/selectors/appSelectors';
import { downloadJson } from '@vlabs/shared/utils';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectDynamicHandlerId } from '@vlabs/pages/handlers/selectors';

import { ResultSectionPanel } from '@vlabs/pages/checks/components/result-section-panel/ResultSectionPanel';

import type { SectionProps } from '../types';
import st from './LivenessSection.module.sass';
import type { TResultStatus } from '../../check-tag/CheckTag';
import { useChecksResultContext } from '../../checksResultContext';
import { Loader } from '../../loader/Loader';
import { EstimationError } from '../estimation-error';
import { FaceQualityResultItem, ImageModificationResultItem, LivenessResultItem } from '../result-items';

function LivenessSection({
  file,
}: SectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const [results, setResults] = useState<object | undefined>(undefined);
  const [resultsJson, setResultsJson] = useState<object | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const { updateCheckResult } = useChecksResultContext();

  const dynamicHandlerId = useSelector(selectDynamicHandlerId);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const lpVersion = useSelector((store) => selectAppService(store, 'lunaPlatform')?.versionNum);

  useEffect(() => {
    if (!file) return;
    if (!dynamicHandlerId) return;
    estimateLivenessDeepfake({
      file,
      handlerId: dynamicHandlerId,
      lpVersion,
      liveness: true,
      raw: true,
    }).then((resp) => {
      setResultsJson(resp);
      if ((resp?.events?.length ?? 0) === 0) setError(t('checks:error.no events'));
      else setResults(resp?.events?.[0]?.detections?.[0]?.samples?.face?.detection);
    }).catch(($error) => {
      if ($error.error_code !== 11038) throw $error;
      setError(t('checks:error.multiple faces') as unknown as string);
    });
  }, [lpVersion, dynamicHandlerId, file, t]);

  useEffect(() => {
    if (!results) return;
    updateCheckResult?.(
      results?.attributes?.liveness?.prediction === 'real'
      && results?.face_quality?.status === 1,
    );
  }, [results, updateCheckResult]);

  const imageModificationResult = results?.face_quality?.checks?.find(({ name }) => name === 'image_modification').result;
  const faceQualityChecks: [] = results?.face_quality?.checks?.filter(({ name }) => name !== 'image_modification');
  const faceQualityChecksStatus: TResultStatus = faceQualityChecks?.reduce((acc, { result }) => acc && result, 1);

  return (
    <ResultSectionPanel
      subtitle={(
        results ? (
          <div className={st.Subtitle}>
            <IconButton
              icon={<DownloadIcon />}
              kind="secondary"
              onClick={() => downloadJson(`${file.name}_liveness`, resultsJson)}
              shape="round"
              size="small"
              variant="dimmed"
            />
          </div>
        ) : undefined
      )}
      title={t('checks:estimations.liveness.titleWithImageModification') as unknown as string}
    >
      <EstimationError error={error} />
      {!!results && (
        <React.Fragment>
          <LivenessResultItem
            prediction={results.attributes.liveness.prediction}
            score={results.attributes.liveness.estimations.score}
          />
          <ImageModificationResultItem
            result={imageModificationResult}
          />
          <FaceQualityResultItem
            checks={faceQualityChecks}
            status={faceQualityChecksStatus}
          />
        </React.Fragment>
      )}
      {!results && !error && <Loader />}
    </ResultSectionPanel>
  );
}

export { LivenessSection };
